/**
 * F36 (Wave 2E) — ReinstateClubDialog.
 *
 * Lighter confirmation (NOT destructive, no type-to-confirm). Optional note
 * saved on the audit log. Composes Modal + react-hook-form, mirroring F29's
 * `ReinstateTeamDialog` + F11's ResumeDialog.
 */
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Modal, Textarea } from '@bravemove/ui-web';
import type { ReinstateClubInput } from '../../api/clubs.js';

export interface ReinstateClubDialogProps {
  readonly open: boolean;
  readonly clubName: string;
  readonly loading: boolean;
  readonly serverError: string | null;
  readonly onClose: () => void;
  readonly onSubmit: (body: ReinstateClubInput) => void;
}

interface ReinstateFormValues {
  /** Optional note saved on the audit trail — the wire field is `note` (TCR-003). */
  readonly note: string;
}

export function ReinstateClubDialog({
  open,
  clubName,
  loading,
  serverError,
  onClose,
  onSubmit,
}: ReinstateClubDialogProps) {
  const { t } = useI18n();

  const localSchema = z.object({ note: z.string().max(500) });

  const form = useForm<ReinstateFormValues>({
    resolver: zodResolver(localSchema),
    defaultValues: { note: '' },
    mode: 'onTouched',
  });

  useEffect(() => {
    if (open) form.reset({ note: '' });
  }, [open, form]);

  const handleConfirm = () => {
    void form.handleSubmit((v) => {
      const trimmed = v.note.trim();
      // The backend ReinstateBody is `{ note? }` (.strict()) — sending any
      // other key 422s, which is exactly the TCR-003 bug this field name fixes.
      onSubmit(trimmed ? { note: trimmed } : {});
    })();
  };

  // ICU `{clubName}` interpolation does NOT fire reliably in test env.
  const interpolated = t('admin:club.reinstate_dialog.title', {
    clubName,
    defaultValue: 'Reinstate {clubName}?',
  }) as string;
  const titleText = interpolated.includes('{clubName}') ? `Reinstate ${clubName}?` : interpolated;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={titleText}
      description={t('admin:club.reinstate_dialog.body')}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            {t('admin:club.reinstate_dialog.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={loading}
            loading={loading}
            data-testid="reinstate-club-confirm"
          >
            {t('admin:club.reinstate_dialog.confirm_button')}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleConfirm();
        }}
        className="flex flex-col gap-4"
      >
        <FormField
          label={t('admin:club.reinstate_dialog.note_label')}
          helper={t('admin:club.reinstate_dialog.note_helper')}
          error={form.formState.errors.note?.message}
        >
          <Textarea
            rows={3}
            maxLength={500}
            showCounter
            data-testid="reinstate-club-reason"
            {...form.register('note')}
          />
        </FormField>

        {serverError ? <Banner tone="danger">{serverError}</Banner> : null}
      </form>
    </Modal>
  );
}
